import Vue from "vue";

import { getCookie } from "@/plugins/auth/utils";

// HTTP calls for upload endpoints and streaming XHR helpers.
const createChunkedUploadSession = (file, filename, seriesId) => {
  const payload = {
    total_size: file.size,
    filename: filename || file.name,
    series: seriesId
  };
  return Vue.$http.post("api/uploads/", null, payload).then(res => res.body);
};

const getChunkedUploadStatus = (uploadId, options = {}) => {
  const includeChunks = options.includeChunks;
  const url =
    includeChunks === false
      ? `api/uploads/${uploadId}/status/?include_chunks=0`
      : `api/uploads/${uploadId}/status/`;
  return Vue.$http.get(url).then(res => {
    if (res && res.body) {
      return res.body;
    }
    if (res && res.data) {
      return res.data;
    }
    return res;
  });
};

const getUploadConfig = () => {
  return Vue.$http.get("api/uploads/config/").then(res => {
    if (res && res.body) {
      return res.body;
    }
    if (res && res.data) {
      return res.data;
    }
    return res;
  });
};

const getUploadAvailability = () => {
  return Vue.$http
    .get("api/uploads/availability/")
    .then(res => {
      if (res && res.body) {
        return res.body;
      }
      if (res && res.data) {
        return res.data;
      }
      return res;
    })
    .catch(error => {
      if (error && error.status === 429) {
        const payload = error.body || error.data || {};
        return { ...payload, allowed: false, http_status: 429 };
      }
      throw error;
    });
};

const uploadChunk = (uploadId, index, blob) => {
  return Vue.http.put(`api/uploads/${uploadId}/chunk/${index}/`, blob, {
    headers: { "Content-Type": "application/octet-stream" },
    timeout: 0
  });
};

const finalizeChunkedUpload = uploadId => {
  return Vue.$http
    .post(`api/uploads/${uploadId}/finalize/`, null, null)
    .then(res => res.body)
    .catch(error => {
      if (error && error.status === 429) {
        if (error.body && error.body.status) {
          // Tag throttled responses so polling can back off.
          return { ...error.body, _throttled: true };
        }
        throw error;
      }
      throw error;
    });
};

const uploadFileSimple = (seriesId, file, options = {}) => {
  const onProgress =
    typeof options.onProgress === "function" ? options.onProgress : null;

  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);
    if (seriesId) {
      formData.append("series", seriesId);
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "api/uploads/simple/");
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      xhr.setRequestHeader("X-CSRFToken", csrfToken);
    }

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = event => {
        if (!event.lengthComputable) {
          return;
        }
        onProgress({
          bytesSent: event.loaded,
          bytesTotal: event.total || file.size
        });
      };
    }

    const parseResponse = () => {
      try {
        return xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (error) {
        return {};
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parseResponse());
        return;
      }
      if (xhr.status === 429) {
        const payload = parseResponse();
        if (payload && payload.status) {
          // Only treat 429 as accepted when the API provides a status payload.
          resolve({ ...payload, _throttled: true });
          return;
        }
      }
      reject(new Error(`Simple upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Simple upload failed"));
    xhr.send(formData);
  });
};

const uploadFileBatch = (seriesId, files, options = {}) => {
  const onProgress =
    typeof options.onProgress === "function" ? options.onProgress : null;
  const fileList = Array.isArray(files) ? files : [];
  const totalBytes = fileList.reduce((sum, file) => sum + (file?.size || 0), 0);

  return new Promise((resolve, reject) => {
    const formData = new FormData();
    fileList.forEach(file => {
      formData.append("files", file);
    });
    if (seriesId) {
      formData.append("series", seriesId);
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "api/uploads/series-batch/");
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      xhr.setRequestHeader("X-CSRFToken", csrfToken);
    }

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = event => {
        if (!event.lengthComputable && !totalBytes) {
          return;
        }
        onProgress({
          bytesSent: event.loaded,
          bytesTotal: event.total || totalBytes
        });
      };
    }
    const buildErrorPayload = () => {
      const payload = parseResponse();
      const base =
        payload && typeof payload === "object" && !Array.isArray(payload)
          ? { ...payload }
          : {};
      if (!Object.keys(base).length) {
        base.error = `Batch upload failed (${xhr.status})`;
      }
      if (xhr.status) {
        base.http_status = xhr.status;
      }
      if (xhr.statusText) {
        base.http_status_text = xhr.statusText;
      }
      return base;
    };

    const parseResponse = () => {
      try {
        return xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (error) {
        return {};
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parseResponse());
        return;
      }
      if (xhr.status === 429) {
        const payload = parseResponse();
        if (payload && payload.status) {
          // Only treat 429 as accepted when the API provides a status payload.
          resolve({ ...payload, _throttled: true });
          return;
        }
      }
      reject(buildErrorPayload());
    };
    xhr.onerror = () =>
      reject({
        error: "Batch upload failed",
        ...(xhr.status ? { http_status: xhr.status } : {}),
        ...(xhr.statusText ? { http_status_text: xhr.statusText } : {})
      });
    xhr.send(formData);
  });
};

export {
  createChunkedUploadSession,
  finalizeChunkedUpload,
  getChunkedUploadStatus,
  getUploadAvailability,
  getUploadConfig,
  uploadChunk,
  uploadFileBatch,
  uploadFileSimple
};
